import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { forwardRef, useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createRequestColumns } from "./requestColumns";
import ActionButton from "../ui/ActionButton";
import {
  faBoxes,
  faEdit,
  faTimes,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import "../../style/RequestsTable.css";

const RequestsTable = forwardRef(
  (
    {
      data,
      darkMode,
      height = 600,
      onAllocate,
      onRequestChange,
      onDecline,
      onSelectionChange,
      externalSelection,
      columnVisibility = {},
    },
    ref
  ) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [selectedRows, setSelectedRows] = useState([]);
    const isInternalUpdate = useRef(false);

    // Sync with external selection
    useEffect(() => {
      if (externalSelection !== undefined && !isInternalUpdate.current) {
        setSelectedRows(externalSelection);
      }
      isInternalUpdate.current = false;
    }, [externalSelection]);

    // Notify parent when selection changes
    useEffect(() => {
      if (onSelectionChange && isInternalUpdate.current) {
        onSelectionChange(selectedRows);
      }
    }, [selectedRows, onSelectionChange]);

    const handleRowClick = (params, event) => {
      isInternalUpdate.current = true;

      if (event.ctrlKey || event.metaKey) {
        setSelectedRows((prev) =>
          prev.includes(params.id)
            ? prev.filter((id) => id !== params.id)
            : [...prev, params.id]
        );
      } else {
        setSelectedRows([params.id]);
      }
    };

    const columns = useMemo(() => {
      const handleViewListing = (listingId) => {
        navigate(`/donor/donate-materials?listing=${listingId}`);
      };

      const baseColumns = createRequestColumns(t, darkMode, columnVisibility);

      const actionsColumn = {
        field: "actions",
        headerName: t("Actions"),
        flex: 1.8,
        minWidth: 180,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const request = params.row;
          const isAllocated = !!request.date_allocated;
          const isExpired =
            request.scheduled_date &&
            new Date(request.scheduled_date) < new Date();

          const showActionButtons =
            !isAllocated && request.active && !isExpired;

          return (
            <div className="actions-cell" onClick={(e) => e.stopPropagation()}>
              {/* View Listing button - always visible */}
              <ActionButton
                variant="default"
                icon={faExternalLinkAlt}
                iconOnly
                tooltip={t("View Listing")}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewListing(request.listing_id);
                }}
              />

              {/* Action buttons - only for active, non-allocated, non-expired */}
              {showActionButtons && (
                <>
                  <ActionButton
                    variant="secondary"
                    icon={faBoxes}
                    iconOnly
                    tooltip={t("Allocate")}
                    onClick={(e) => {
                      e.stopPropagation();
                      const quantity = request.quantity || request.min_qty;
                      onAllocate(request.id, quantity);
                    }}
                  />
                  <ActionButton
                    variant="default"
                    icon={faEdit}
                    iconOnly
                    tooltip={t("Request Changes")}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onRequestChange) {
                        onRequestChange(request.id);
                      }
                    }}
                  />
                  <ActionButton
                    variant="danger"
                    icon={faTimes}
                    iconOnly
                    tooltip={t("Decline")}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDecline(request.id);
                    }}
                  />
                </>
              )}
            </div>
          );
        },
      };

      return [...baseColumns, actionsColumn];
    }, [
      t,
      darkMode,
      onAllocate,
      onDecline,
      onRequestChange,
      columnVisibility,
      navigate,
    ]);

    const dataGridStyles = useMemo(
      () => ({
        border: "none",
        height,
        fontFamily: '"Poppins", sans-serif',
        userSelect: "text",
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },
        "& .MuiDataGrid-cell": {
          cursor: "text",
          userSelect: "text",
          display: "flex",
          alignItems: "center",
        },
        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
          {
            outline: "none",
          },
      }),
      [height]
    );

    const getRowClassName = (params) => {
      return selectedRows.includes(params.id) ? "Mui-selected" : "";
    };

    return (
      <DataGrid
        rows={data}
        columns={columns}
        disableColumnResize
        disableColumnMenu
        hideFooter
        disableRowSelectionOnClick
        getRowClassName={getRowClassName}
        onRowClick={handleRowClick}
        sortingOrder={["desc", "asc"]}
        initialState={{
          sorting: {
            sortModel: [{ field: "status", sort: "desc" }],
          },
        }}
        className="requests-table"
        sx={dataGridStyles}
      />
    );
  }
);

RequestsTable.displayName = "RequestsTable";

export default RequestsTable;
